import React, { useEffect, useMemo, useState } from "react";
import { AlertCircle, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  useProcessingIndicator,
  type ProcessingIndicator,
} from "@/components/application/ProcessingToast";
import type { ProgressState } from "@/hooks/useBorrowerRealtime";

const FAILED_INFO_DISMISS_MS = 8000;

interface BorrowerProcessingBannersProps {
  borrowerId: number | null;
  isInitialProcessing: boolean;
  failedStatementCount: number;
  allStatementsFailed: boolean;
  underwritingProgress: ProgressState | null;
  taskError: string | null;
  onDismissTaskError: () => void;
}

const TERMINAL_PROGRESS = new Set(["completed", "error", "failed"]);

const failedStatementLabel = (count: number) =>
  count === 1 ? "1 statement failed to process." : `${count} statements failed to process.`;

const BorrowerProcessingBanners: React.FC<BorrowerProcessingBannersProps> = ({
  borrowerId,
  isInitialProcessing,
  failedStatementCount,
  allStatementsFailed,
  underwritingProgress,
  taskError,
  onDismissTaskError,
}) => {
  const [showFailedInfo, setShowFailedInfo] = useState(false);

  const indicator = useMemo<ProcessingIndicator | null>(() => {
    if (isInitialProcessing) {
      if (allStatementsFailed) return null;
      return {
        title: "Processing Application",
        description:
          underwritingProgress?.message ||
          "Your bank statements are being analyzed. This usually takes 1-2 minutes.",
        startedAt: underwritingProgress?.startedAt,
        elapsedSeconds: underwritingProgress?.elapsedSeconds,
      };
    }
    if (!underwritingProgress || TERMINAL_PROGRESS.has(underwritingProgress.status)) {
      return null;
    }
    return {
      title:
        underwritingProgress.taskType === "transaction_enrichment"
          ? "Transaction Enrichment"
          : "Underwriting Analysis",
      description: underwritingProgress.message,
      startedAt: underwritingProgress.startedAt,
      elapsedSeconds: underwritingProgress.elapsedSeconds,
    };
  }, [allStatementsFailed, isInitialProcessing, underwritingProgress]);

  useProcessingIndicator(indicator);

  useEffect(() => {
    if (!borrowerId || failedStatementCount < 1 || allStatementsFailed) {
      setShowFailedInfo(false);
      return;
    }

    setShowFailedInfo(true);
    const timer = window.setTimeout(() => setShowFailedInfo(false), FAILED_INFO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [borrowerId, failedStatementCount, allStatementsFailed]);

  return (
    <>
      <Collapsible open={showFailedInfo}>
        <CollapsibleContent>
          <Alert className="mb-6 border-sky-200 bg-sky-50 text-sky-950 [&>svg]:text-sky-700">
            <Info className="h-4 w-4" />
            <AlertDescription>{failedStatementLabel(failedStatementCount)}</AlertDescription>
          </Alert>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={allStatementsFailed}>
        <CollapsibleContent>
          <Card className="mb-6 border-destructive/20 bg-destructive/5 p-6 shadow-none">
            <div className="mb-3 flex items-center">
              <AlertCircle className="mr-2 h-5 w-5 text-destructive" />
              <h2 className="text-lg font-semibold text-destructive">
                Statement Processing Failed
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              {failedStatementLabel(failedStatementCount)} Re-upload bank statements to retry
              analysis.
            </p>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={!!taskError}>
        <CollapsibleContent>
          <Alert variant="destructive" className="relative mb-6 pr-10">
            <AlertDescription>{taskError}</AlertDescription>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-2 top-2 h-7"
              onClick={onDismissTaskError}
            >
              Dismiss
            </Button>
          </Alert>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
};

export default BorrowerProcessingBanners;
